import { createSelector } from 'reselect';
import get from 'lodash/get';
import { MetricsInfo, metricKeys, MetricTypes } from '@pulse/metrics';
import moment from 'moment';
import {
  getAllActiveMetrics,
  getAllSortedMetrics,
  RT_ON_TRENDS_EXCLUDE_LIST,
} from '@utils/metrics';
import featureNames from '@utils/featureToggle/constants';
import {
  CURRENT_PRODUCT_MODE,
  NO_COMPARE_MODE,
  TAB_TRENDS_KEY,
  ADMIN_ROLE_NAME,
  STAFF_ROLE_NAME,
  CS_ROLE_NAME,
  DATE_SHORTCUTS,
  CDN_ACCOUNT_TYPE,
  isAppModule,
} from '@utils/constants';
import { getMetricExcludeList, excludeWithMetricDeDuplication } from '@utils/metrics/metricHelpers';
import metricsStore from '@globalStore/metricsStore';
import { getDistributionMetrics } from '@components/metrics-panel-hz/utils/metricsMapping';
import { SCOPE } from './types';

const selector = (store: any) => get(store, SCOPE);

export default selector;

export const intervalSelector = createSelector(selector, (commonStore) => commonStore.interval);

export const timeZoneOffsetSelector = createSelector(selector, (store) =>
  get(store, 'tzOffset', null),
);

export const getUserProfile = createSelector(
  selector,
  (store) =>
    get(store, 'profiles', []).find((profile: { is_selected: any }) => profile.is_selected) || {},
);

export const selectedCustomerIdSelector = createSelector(getUserProfile, (profile) =>
  get(profile, 'customer_id', -1),
);

export const selectedCustomerNameSelector = createSelector(getUserProfile, (profile) =>
  get(profile, 'customer_name', ''),
);

export const selectedUserNameSelector = createSelector(getUserProfile, (profile) =>
  get(profile, 'username', ''),
);

export const selectedUserRoleSelector: any = createSelector(getUserProfile, (profile) =>
  get(profile, 'role', ''),
);

export const currentMetricsMetaSelector = createSelector(selector, (store) => {
  const { combinedMetricsMeta } = metricsStore.getState();
  return combinedMetricsMeta[CURRENT_PRODUCT_MODE.toLowerCase()];
});

export const activeMetricsSelector: any = createSelector(
  currentMetricsMetaSelector,
  (currentMetricsMeta) => {
    if (!currentMetricsMeta) {
      return {};
    }
    return getAllActiveMetrics(currentMetricsMeta);
  },
);

export const distributionMapSelector = createSelector(
  currentMetricsMetaSelector,
  (currentMetricsMeta: any) => {
    const distributionMetrics: any = Object.values(currentMetricsMeta)
      .filter((it: MetricsInfo) => it.name && it.distribution)
      .reduce((acc: any, it: MetricsInfo) => {
        return {
          ...acc,
          [it.name]: it.distribution,
        };
      }, {});
    return {
      ...distributionMetrics,
      ...getDistributionMetrics(),
    };
  },
);

export const activeSortedMetricsSelector: any = createSelector(
  activeMetricsSelector,
  (activeMetrics) => getAllSortedMetrics(activeMetrics),
);

export const metricsByTypesSelector: any = createSelector(
  currentMetricsMetaSelector,
  (metricList) => {
    const typeGroupedMetrics = {};
    // metricList not initialized in initial state to avoid errors or`ed with empty object
    Object.keys(metricList || {}).forEach((metric: any) => {
      typeGroupedMetrics[metricList[metric].name] = metricList[metric].type;
    });
    return typeGroupedMetrics;
  },
);

export const dimensionTypeInFilterSelector = createSelector(
  selector,
  (store) => store.dimensionTypeInFilter,
);

export const isAdminRoleSelector = createSelector(
  selectedUserRoleSelector,
  (role: string) => role === ADMIN_ROLE_NAME,
);

export const isStaffRoleSelector = createSelector(
  selectedUserRoleSelector,
  (role: string) => role === STAFF_ROLE_NAME,
);

export const isCustomerServiceRoleSelector = createSelector(
  selectedUserRoleSelector,
  (role: string) => role === CS_ROLE_NAME,
);

export const filterSelector = createSelector(selector, (store) => store.appliedFilters[0].rule);

export const filterColorSelector = createSelector(selector, (store) => store.filterColor);

export const compareModeSelector = createSelector(selector, (store) => store.compareMode);

export const getFirstAppliedFilter = createSelector(selector, (commonStore) =>
  get(commonStore, 'appliedFilters[0]', {}),
);

export const presetFidSelector: any = createSelector(
  getFirstAppliedFilter,
  (filter) => filter.presetFid || null,
);

// true if compare modal is active
export const isCompareModalSelector = createSelector(
  selector,
  (commonStore) => commonStore.compareMode !== NO_COMPARE_MODE,
);

export const getActiveTabId = createSelector(selector, (commonStore) =>
  get(commonStore, 'dashboard.activeId', null),
);

// call with featureName as param
export const featureValueSelector = createSelector(
  selector,
  (_: any, featureName: any) => featureName,
  (commonStore: any, featureName: any) => get(commonStore, ['features', featureName], null),
);

export const featureSelector = (featureName: string) =>
  createSelector(selector, (commonStore) => get(commonStore, ['features', featureName], null));

export const realTimeOnTrendsFeatureSelector = featureSelector(featureNames.RT_ON_TRENDS_LAST_15);

export const appAIAlertFeatureFlagSelector = featureSelector(featureNames.APP_AI_ALERTS);

// TODO: since this is used on compare, move it out of common
export const liveModeSelectedSelector = createSelector(selector, (commonStore) =>
  get(commonStore, 'isLiveModeSelected', false),
);

export const realTimeModeOnTrendsSelector: any = createSelector(
  intervalSelector,
  realTimeOnTrendsFeatureSelector,
  (interval, realTimeOnTrends15Mins) => {
    const shortcut = interval.shortcut;
    return shortcut === DATE_SHORTCUTS.last15Mins && (realTimeOnTrends15Mins || isAppModule);
  },
);

export const supportedMetricsSelector: any = createSelector(
  selector,
  presetFidSelector,
  getActiveTabId,
  realTimeModeOnTrendsSelector,
  (commonStore: { features: any }, presetFid: any, activeTabId: string, realTimeOnTrends: any) => {
    const { features }: any = commonStore;
    const { combinedMetricsKey, combinedMetricsMeta } = metricsStore.getState();
    let baseMetrics: any = combinedMetricsKey[CURRENT_PRODUCT_MODE.toLowerCase()];
    // filter based on preset filter toggle
    if (activeTabId === TAB_TRENDS_KEY) {
      baseMetrics = excludeWithMetricDeDuplication(baseMetrics, getMetricExcludeList(presetFid));
    }
    if (realTimeOnTrends) {
      baseMetrics = baseMetrics.filter((metric) => !RT_ON_TRENDS_EXCLUDE_LIST.includes(metric));
    }

    const productMetricsMeta = combinedMetricsMeta[CURRENT_PRODUCT_MODE.toLowerCase()];
    return baseMetrics
      .filter((metric) => features[featureNames.METRICS].includes(metric))
      .reduce((res, metric) => {
        res[metric] = productMetricsMeta[metric];
        return res;
      }, {});
  },
);

export const supportedSortedMetricsSelector: any = createSelector(
  selector,
  presetFidSelector,
  getActiveTabId,
  activeSortedMetricsSelector,
  (commonStore, presetFid, activeTabId, activeSortedMetrics) => {
    const { features } = commonStore;

    let baseMetrics: any[] = Object.keys(activeSortedMetrics);

    // filter based on preset filter toggle
    if (activeTabId === TAB_TRENDS_KEY) {
      baseMetrics = excludeWithMetricDeDuplication(baseMetrics, getMetricExcludeList(presetFid));
    }

    return baseMetrics
      .filter((metric) => features[featureNames.METRICS].includes(metric))
      .reduce((res, metric) => {
        res[metric] = activeSortedMetrics[metric];
        return res;
      }, {});
  },
);

// depricated feature flag
export const newTrendsLayoutFeatureSelector = () => true;

export const nocTechErrorMetricsFeatureSelector = featureSelector(
  featureNames.NOC_BUSINESS_ERROR_METRICS,
);

export const pulse5ApiUsageDashboardFeatureSelector = featureSelector(
  featureNames.PULSE4_API_USAGE,
);

export const siBigpandaFeatureSelector = featureSelector(featureNames.SI_BIGPANDA);

export const siDatadogFeatureSelector = featureSelector(featureNames.SI_DATADOG);

export const siPagerDutyFeatureSelector = featureSelector(featureNames.EI_PAGERDUTY);

export const siPagerDutyDisplayFeatureSelector = featureSelector(
  featureNames.DISPLAY_PAGERDUTY_IN_SI,
);

export const kpiDropdownFeatureSelector = featureSelector(featureNames.KPI_DROPDOWN);

export const granularityFeatureSelector = featureSelector(featureNames.TIME_GRANULARITY);

export const tlbCutoverDateSelector = createSelector(selector, (commonStore) => {
  const utcDate = moment.utc(get(commonStore, ['features', featureNames.TLB_CUTOVER_DATE], null));
  if (utcDate.isValid()) {
    return utcDate.toISOString();
  }
  return null;
});

export const ovbDeviceMetricsEnabledSelector = createSelector(selector, ({ features }) => {
  const metricsList = features[featureNames.METRICS];
  return (
    metricsList.includes(metricKeys.GoodUniqueDevices) &&
    metricsList.includes(metricKeys.BadUniqueDevices)
  );
});

export const ovbViewersMetricsEnabledSelector = createSelector(selector, ({ features }) => {
  const metricsList = features[featureNames.METRICS];
  return (
    metricsList.includes(metricKeys.GoodUniqueViewers) &&
    metricsList.includes(metricKeys.BadUniqueViewers)
  );
});

export const dimensionRowLimitSelector = createSelector(selector, ({ features }) => {
  const {
    [featureNames.ENUMERATED_VALUES_PER_DIMENSION]: limit = 200,
    [featureNames.ERROR_CODES_LIMIT]: errorLimit = 200,
  } = features;
  return { limit: Math.min(limit, 500), errorLimit: Math.min(errorLimit, 500) };
});

export const autoRetrySelector = featureSelector(featureNames.AUTO_RETRY);
export const bleEnabledSelector = featureSelector(featureNames.BUSINESS_ERROR_METRIC);
export const benchmarkSelector = featureSelector(featureNames.EXPERIENCE_DASHBOARD_BENCHMARK);
export const advanceBenchmarkSelector = featureSelector(
  featureNames.EXPERIENCE_DASHBOARD_ADVANCED_BENCHMARK,
);

export const unifiedAdContentOverviewSelector = featureSelector(
  featureNames.UNIFIED_AD_CONTENT_OVERVIEW,
);

export const hasBenchmarkSelector = createSelector(selector, (commonStore) => {
  const { features } = commonStore;
  const benchmarkFlag = features[featureNames.EXPERIENCE_DASHBOARD_BENCHMARK];
  const advancedBenchmarkFlag = features[featureNames.EXPERIENCE_DASHBOARD_ADVANCED_BENCHMARK];
  return benchmarkFlag || advancedBenchmarkFlag;
});
// metrics avaialble for the account
// also includes metrics with active:false
export const allSupportedMetricsSelector: any = createSelector(
  selector,
  currentMetricsMetaSelector,
  (commonStore, currentMetricsMeta) => {
    const { features } = commonStore;
    const featureMetrics = features[featureNames.METRICS];

    const customerMetrics = {};
    featureMetrics.forEach((m: string) => {
      if (m in currentMetricsMeta) {
        customerMetrics[m] = currentMetricsMeta[m];
      }
    });
    return customerMetrics;
  },
);

export const adContentMetricsSelector = createSelector(allSupportedMetricsSelector, (metrics) => {
  const adContentMetricsList = {};
  Object.keys(metrics).forEach((m) => {
    if (metrics[m].type === MetricTypes.adContentMetrics) {
      adContentMetricsList[m] = metrics[m];
    }
  });
  return adContentMetricsList;
});

// additional feature based filter for ad-content
export const adContentFilteredMetrics: any = createSelector(
  allSupportedMetricsSelector,
  unifiedAdContentOverviewSelector,
  adContentMetricsSelector,
  (metrics, unifiedAdContentOverview, adContentMetrics) => {
    if (unifiedAdContentOverview) {
      return metrics;
    }

    const filteredList = {};
    Object.keys(metrics).forEach((m) => {
      if (!(m in adContentMetrics)) {
        filteredList[m] = metrics[m];
      }
    });
    return filteredList;
  },
);

export const aiAlertsDimensionMappingSelector = createSelector(
  selector,
  (commonStore) => commonStore.aiAlertsDimensionMapping,
);

export const layoutSelector = createSelector(selector, (commonStore) => commonStore.layout);

// force new trends
export const horizontalLayoutSelector = createSelector(layoutSelector, (layout) => true);

export const precisionPolicySchedulingFeatureSelector = featureSelector(
  featureNames.PRECISION_POLICY_SCHEDULING,
);

export const advancedViewerModuleFeatureSelector = featureSelector(
  featureNames.ADVANCED_VIEWER_MODULE_ENABLED,
);

export const show90DaysViewerDataSelector = featureSelector(featureNames.SHOW_NINETY_DAYS_HISTORY);

export const autoInsightsFeatureSelector = featureSelector(featureNames.AUTO_INSIGHTS);

export const aiAlertsP5FeatureSelector = featureSelector(featureNames.AI_ALERTS_PULSE5);

export const aiAlerts1minPersistenceSelector = featureSelector(
  featureNames.AI_ALERTS_1_MIN_PERSISTENCE,
);

export const aiAlertsConfigSelector = featureSelector(featureNames.AI_ALERTS_CONFIG);

export const aiAlertsAddCustomDimensionsSelector = featureSelector(
  featureNames.AI_ALERTS_ADD_CUSTOM_DIMENSIONS,
);

export const aiAlertsCustomDimensionsUpperLimitLevel1Selector = featureSelector(
  featureNames.AI_ALERTS_CUSTOM_DIMENSIONS_UPPER_LIMIT_LEVEL_1,
);

export const aiAlertsCustomDimensionsUpperLimitLevel2Selector = featureSelector(
  featureNames.AI_ALERTS_CUSTOM_DIMENSIONS_UPPER_LIMIT_LEVEL_2,
);

export const aiAlertsCustomDimensionsUpperLimitLevel3Selector = featureSelector(
  featureNames.AI_ALERTS_CUSTOM_DIMENSIONS_UPPER_LIMIT_LEVEL_3,
);

export const sessionTimeline30DaysSaveFeatureSelector = featureSelector(
  featureNames.SESSION_TIMELINE_30DAYS_SAVE,
);

export const isAiAlertsCustomMetricsEnabled = featureSelector(
  featureNames.AI_ALERTS_CUSTOM_METRICS,
);

export const aiAlertsCustomMetricsLimit = featureSelector(
  featureNames.AI_ALERTS_CUSTOM_METRICS_LIMIT,
);

export const aiBrokenAssetsSelector = featureSelector(featureNames.BROKEN_ASSETS);

export const accountTypeSelector = featureSelector(featureNames.CUSTOMER_ACCOUNT_TYPE);

export const aiAlertsPCPFeatureSelector = featureSelector(featureNames.AI_ALERTS_PCP);

export const aiAlertsCIRRFeatureSelector = featureSelector(featureNames.AI_ALERTS_CIRR);

export const viewersOvbFeatureSelector = featureSelector(featureNames.VIEWERS_OVERVIEW);

export const viewersBrowserTabFeatureSelector = featureSelector(featureNames.VIEWERS_BROWSER_TAB);

export const aiNewappVersionSelector = featureSelector(featureNames.NEWAPP_VERSION);

// enable annotations if feature is enabled
// and user preference is set
// TODO: cleanup checks after GA
export const annotationsFeatureSelector = featureSelector(featureNames.ANNOTATIONS);

// depricated feature flag
export const horizontalLayoutFeatureSelector = () => true;

export const publicSavedDashboardsSelector = featureSelector(featureNames.PUBLIC_SAVED_DASHBOARDS);

export const customerAccountTypeSelector = featureSelector(featureNames.CUSTOMER_ACCOUNT_TYPE);

export const isCDNAccountSelector = createSelector(
  customerAccountTypeSelector,
  (customerAccountType) => customerAccountType === CDN_ACCOUNT_TYPE,
);

export const realtimePSDSelector = featureSelector(featureNames.REALTIME_PSD);

export const appLiveModeOnTrendsSelector = featureSelector(featureNames.APP_RT_ON_TRENDS);

export const appDistributionSelector = featureSelector(featureNames.APP_INSIGHTS_DISTRIBUTION);

export const appMetricFilteringSelector = featureSelector(
  featureNames.APP_INSIGHTS_METRIC_FILTERING,
);

export const appExperienceShareAndSaveSelector = featureSelector(
  featureNames.APP_EXPERIENCE_SAVE_AND_SHARE,
);

export const isAppSPIEnabledSelector = featureSelector(featureNames.APP_SPI);

export const appSiPagerDutyFeatureSelector = featureSelector(featureNames.APP_PAGERDUTY);

export const promptFeatureSelector = featureSelector(featureNames.PROMPT);

export const isDecodedStackTraceEnabledSelector = featureSelector(featureNames.DECODED_STACK_TRACE);

export const rcaFeatureSelector = featureSelector(featureNames.ECO_NETWORK_REQUEST_RCA);

export const isEcoSankeyEnabledSelector = featureSelector(featureNames.ECO_SANKEY);

export const isEcoFunnelEnabledSelector = featureSelector(featureNames.ECO_FUNNEL);

export const isResiliencyEnabledSelector = featureSelector(featureNames.RESILIENCY);

export const aiAlertsMetricsMetaSelector = createSelector(
  selector,
  (store) => store.aiAlertsMetricsMeta,
);

export const aiAlertsSensitivitySelector = createSelector(
  selector,
  (store) => store.aiAlertsSensitivity,
);
